const CLIENT_KEY = 'apps_logs_client_id';
const SESSION_KEY = 'apps_logs_session_id';
const DEFAULT_REDACT_KEYS = ['authorization', 'cookie', 'password', 'token', 'access_token', 'refresh_token', 'id_token', 'secret', 'api_key', 'apikey'];
const DEFAULT_REDACT_PATTERNS = [/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g];
const DEFAULT_MAX_STRING_LENGTH = 5000;
const DEFAULT_MAX_OBJECT_DEPTH = 8;
const DEFAULT_MAX_ARRAY_LENGTH = 50;
const AUTH_PROVIDER_ERROR_DIAGNOSTIC_INTERVAL_MS = 60000;
function id(prefix) {
    return `${prefix}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
function getStoredId(key, prefix, storage) {
    const existing = storage.getItem(key);
    if (existing)
        return existing;
    const next = id(prefix);
    storage.setItem(key, next);
    return next;
}
function truncate(value, max = DEFAULT_MAX_STRING_LENGTH) {
    return value.length > max ? `${value.slice(0, max)}…` : value;
}
function sanitize(value, denylist = DEFAULT_REDACT_KEYS, patterns = DEFAULT_REDACT_PATTERNS, seen = new WeakSet(), depth = 0, maxStringLength = DEFAULT_MAX_STRING_LENGTH, maxObjectDepth = DEFAULT_MAX_OBJECT_DEPTH, maxArrayLength = DEFAULT_MAX_ARRAY_LENGTH) {
    if (typeof value === 'string') {
        const redacted = patterns.reduce((next, pattern) => next.replace(pattern, '[REDACTED]'), value);
        return redacted.length > maxStringLength ? `${redacted.slice(0, maxStringLength)}…` : redacted;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || value == null)
        return value;
    if (value instanceof Error)
        return sanitize({ name: value.name, message: value.message, stack: value.stack }, denylist, patterns, seen, depth, maxStringLength, maxObjectDepth, maxArrayLength);
    if (value instanceof Date)
        return value.toISOString();
    if (typeof value !== 'object')
        return String(value);
    if (depth >= maxObjectDepth)
        return '[MaxDepth]';
    if (seen.has(value))
        return '[Circular]';
    seen.add(value);
    if (Array.isArray(value)) {
        const sliced = value.length > maxArrayLength ? value.slice(0, maxArrayLength) : value;
        return sliced.map((item) => sanitize(item, denylist, patterns, seen, depth + 1, maxStringLength, maxObjectDepth, maxArrayLength));
    }
    const blocked = new Set(denylist.map((key) => key.toLowerCase()));
    return Object.entries(value).reduce((result, [key, item]) => {
        result[key] = blocked.has(key.toLowerCase()) ? '[REDACTED]' : sanitize(item, denylist, patterns, seen, depth + 1, maxStringLength, maxObjectDepth, maxArrayLength);
        return result;
    }, {});
}
function safeDsnOrigin(dsn) {
    try {
        return new URL(dsn, typeof window !== 'undefined' ? window.location.href : undefined).origin;
    }
    catch {
        return 'unknown';
    }
}
function globMatches(value, pattern) {
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    return new RegExp(`^${escaped}$`, 'i').test(value);
}
function hashString(value) {
    let hash = 5381;
    for (let i = 0; i < value.length; i += 1)
        hash = ((hash << 5) + hash) ^ value.charCodeAt(i);
    return (hash >>> 0).toString(36);
}
function snapshotFor(provider, onError) {
    if (!provider)
        return undefined;
    try {
        return provider.getSnapshot();
    }
    catch (error) {
        onError?.(error);
        return { error: error instanceof Error ? error.message : String(error) };
    }
}
function normalizeUrl(input) {
    try {
        const raw = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input instanceof Request ? input.url : undefined;
        if (!raw)
            return undefined;
        const url = new URL(raw, typeof window !== 'undefined' ? window.location.href : undefined);
        return `${url.origin}${url.pathname}`;
    }
    catch {
        return typeof input === 'string' ? input.split('?')[0] : undefined;
    }
}
function requestMethod(input, init) {
    return (init?.method || (input instanceof Request ? input.method : undefined) || 'GET').toUpperCase();
}
function isOwnIngestRequest(url, dsn) {
    if (!url || !dsn)
        return false;
    try {
        const eventUrl = new URL(url, window.location.href);
        const ingestUrl = new URL(dsn, window.location.href);
        return eventUrl.origin === ingestUrl.origin && eventUrl.pathname === ingestUrl.pathname;
    }
    catch {
        return url === dsn;
    }
}
function isIgnoredUrl(url, ignoredUrls) {
    if (!url || !ignoredUrls || ignoredUrls.length === 0)
        return false;
    return ignoredUrls.some((rule) => {
        if (typeof rule === 'string')
            return url.includes(rule);
        return rule.test(url);
    });
}
export function createLogger(options) {
    const queue = [];
    const tags = {};
    const context = {};
    const restoreFns = [];
    const dedupeMap = new Map();
    let user;
    let authProvider;
    let timer;
    let rateSummaryTimer;
    let lastActivityAt = new Date();
    let lastHiddenAt;
    let consecutiveFlushFailures = 0;
    let rateWindowStartedAt = Date.now();
    let eventsQueuedThisMinute = 0;
    let rateLimitedDropCount = 0;
    let lastAuthProviderErrorDiagnosticAt = 0;
    let currentScreen;
    const clientId = typeof localStorage !== 'undefined' ? getStoredId(CLIENT_KEY, 'client', localStorage) : id('client');
    const sessionId = typeof sessionStorage !== 'undefined' ? getStoredId(SESSION_KEY, 'session', sessionStorage) : id('session');
    const allRedactKeys = [...DEFAULT_REDACT_KEYS, ...(options.redactKeys || [])];
    const allRedactPatterns = [...DEFAULT_REDACT_PATTERNS, ...(options.redactPatterns || [])];
    const maxStringLength = options.maxStringLength || DEFAULT_MAX_STRING_LENGTH;
    const maxObjectDepth = options.maxObjectDepth || DEFAULT_MAX_OBJECT_DEPTH;
    const maxArrayLength = options.maxArrayLength || DEFAULT_MAX_ARRAY_LENGTH;
    function doSanitize(value) {
        return sanitize(value, allRedactKeys, allRedactPatterns, new WeakSet(), 0, maxStringLength, maxObjectDepth, maxArrayLength);
    }
    function emitAuthProviderError(error) {
        const now = Date.now();
        if (now - lastAuthProviderErrorDiagnosticAt < AUTH_PROVIDER_ERROR_DIAGNOSTIC_INTERVAL_MS)
            return;
        lastAuthProviderErrorDiagnosticAt = now;
        push({
            level: 'warn',
            type: 'log',
            event: 'sdk.auth_provider_error',
            message: 'Auth provider getSnapshot() failed',
            metadata: { error: error instanceof Error ? error.message : String(error) }
        }, { internal: true, skipDedupe: true, skipRateLimit: true, skipAutoFlush: true, skipSnapshot: true });
    }
    function authSnapshot() {
        return snapshotFor(authProvider, emitAuthProviderError);
    }
    function serializeConsoleArg(value) {
        return doSanitize(value);
    }
    function stringifyConsoleArg(value) {
        if (typeof value === 'string')
            return value;
        try {
            return JSON.stringify(value);
        }
        catch {
            return String(value);
        }
    }
    function idleMetadata() {
        return {
            lastActivityAt: lastActivityAt.toISOString(),
            idleMs: Date.now() - lastActivityAt.getTime(),
            lastHiddenAt: lastHiddenAt?.toISOString(),
            hiddenMs: lastHiddenAt ? Date.now() - lastHiddenAt.getTime() : undefined,
            visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown'
        };
    }
    function shouldAttachSnapshot(event) {
        if (!authProvider)
            return false;
        if (event.level === 'error' || event.level === 'fatal')
            return true;
        if (event.type === 'network' && event.http?.status === 401)
            return true;
        if ((options.snapshotOn || []).includes(event.event))
            return true;
        return false;
    }
    function enqueueRateLimitSummary() {
        if (!rateLimitedDropCount)
            return;
        const dropped = rateLimitedDropCount;
        rateLimitedDropCount = 0;
        push({
            level: 'warn',
            type: 'log',
            event: 'sdk.events_dropped',
            message: `${dropped} events dropped by SDK rate limit`,
            metadata: { dropped, reason: 'rate_limit', windowMs: 60000 }
        }, { internal: true, skipDedupe: true, skipRateLimit: true, skipAutoFlush: true });
    }
    function enforceRateLimit(internal) {
        if (internal || !options.maxEventsPerMinute)
            return true;
        const now = Date.now();
        if (now - rateWindowStartedAt >= 60000) {
            enqueueRateLimitSummary();
            rateWindowStartedAt = now;
            eventsQueuedThisMinute = 0;
            if (rateSummaryTimer)
                clearTimeout(rateSummaryTimer);
            rateSummaryTimer = undefined;
        }
        eventsQueuedThisMinute += 1;
        if (eventsQueuedThisMinute <= options.maxEventsPerMinute)
            return true;
        rateLimitedDropCount += 1;
        if (!rateSummaryTimer) {
            rateSummaryTimer = setTimeout(() => {
                enqueueRateLimitSummary();
                rateWindowStartedAt = Date.now();
                eventsQueuedThisMinute = 0;
                rateSummaryTimer = undefined;
            }, Math.max(0, 60000 - (now - rateWindowStartedAt)));
        }
        return false;
    }
    function dedupeKeyFor(event) {
        return `${event.fingerprint || event.event}|${event.message ?? ''}`;
    }
    function pruneDedupeMap(now) {
        if (!options.dedupeWindowMs || options.dedupeWindowMs <= 0)
            return;
        for (const [key, entry] of dedupeMap) {
            if (now - entry.firstSeenAt > options.dedupeWindowMs)
                dedupeMap.delete(key);
        }
        const maxEntries = Math.max(options.maxQueueSize || 500, 1000) * 2;
        while (dedupeMap.size > maxEntries) {
            const oldestKey = dedupeMap.keys().next().value;
            if (!oldestKey)
                break;
            dedupeMap.delete(oldestKey);
        }
    }
    function push(event, controls = {}) {
        const now = Date.now();
        if (!controls.skipRateLimit && !enforceRateLimit(controls.internal))
            return;
        const shouldDedupe = !controls.skipDedupe && !!options.dedupeWindowMs && options.dedupeWindowMs > 0;
        const dedupeKey = dedupeKeyFor(event);
        if (shouldDedupe)
            pruneDedupeMap(now);
        if (shouldDedupe) {
            const existing = dedupeMap.get(dedupeKey);
            if (existing && now - existing.firstSeenAt <= options.dedupeWindowMs) {
                existing.lastSeenAt = now;
                existing.count += 1;
                if (existing.event && queue.includes(existing.event))
                    existing.event.dedupedCount = (existing.event.dedupedCount || 0) + 1;
                return;
            }
        }
        const maxQueueSize = options.maxQueueSize || 500;
        const withDefaults = { ...event, eventId: event.eventId || id('event'), timestamp: event.timestamp || new Date().toISOString(), idle: event.idle || idleMetadata() };
        if (!controls.skipSnapshot && shouldAttachSnapshot(withDefaults))
            withDefaults.auth = { ...(authSnapshot() || {}), ...(withDefaults.auth || {}) };
        // First sanitize pass
        const sanitized = doSanitize(withDefaults);
        // beforeSend hook (receives sanitized event)
        const afterHook = options.beforeSend ? options.beforeSend(sanitized) : sanitized;
        if (!afterHook)
            return;
        // Second sanitize pass in case beforeSend added unsafe data
        const finalEvent = doSanitize(afterHook);
        queue.push(finalEvent);
        if (shouldDedupe)
            dedupeMap.set(dedupeKey, { firstSeenAt: now, lastSeenAt: now, count: 0, event: finalEvent });
        if (queue.length > maxQueueSize) {
            const dropped = queue.length - maxQueueSize;
            queue.splice(0, dropped);
            if (!controls.internal)
                push({ level: 'warn', type: 'log', event: 'sdk.queue_full', message: `${dropped} events dropped because SDK queue is full`, metadata: { dropped, maxQueueSize } }, { internal: true, skipDedupe: true, skipRateLimit: true, skipAutoFlush: true });
        }
        if (!controls.skipAutoFlush && (queue.length >= (options.maxBatchSize || 50) || event.level === 'fatal' || event.level === 'error'))
            void flush();
    }
    async function flush() {
        if (queue.length === 0)
            return;
        const events = queue.splice(0, options.maxBatchSize || 50);
        // Sanitize envelope-level fields too
        const payload = JSON.stringify({
            sdk: { name: '@apps-logs/browser', version: '0.1.0' },
            app: { environment: options.environment, release: options.release },
            client: { id: clientId },
            session: { id: sessionId },
            user: user ? doSanitize(user) : undefined,
            tags: tags ? doSanitize(tags) : undefined,
            context: context ? doSanitize(context) : undefined,
            device: doSanitize(collectDevice()),
            events
        });
        try {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                const sent = navigator.sendBeacon(options.dsn, new Blob([payload], { type: 'application/json' }));
                if (sent)
                    return;
            }
            await fetch(options.dsn, { method: 'POST', headers: { 'content-type': 'application/json' }, body: payload, keepalive: true });
            consecutiveFlushFailures = 0;
        }
        catch {
            consecutiveFlushFailures += 1;
            queue.unshift(...events.slice(0, 25));
            if (consecutiveFlushFailures >= 2) {
                push({ level: 'warn', type: 'log', event: 'sdk.flush_failed', message: 'SDK flush failed repeatedly', metadata: { consecutiveFailures: consecutiveFlushFailures, retainedEvents: Math.min(events.length, 25) } }, { internal: true, skipDedupe: true, skipRateLimit: true, skipAutoFlush: true });
            }
        }
    }
    function captureConsole() {
        const global = globalThis;
        if (typeof console === 'undefined')
            return;
        if (global.__appsLogsConsoleWrapped)
            return;
        global.__appsLogsConsoleWrapped = true;
        ['debug', 'info', 'warn', 'error'].filter((method) => (options.consoleLevels || ['debug', 'info', 'warn', 'error']).includes(method)).forEach((method) => {
            const original = console[method];
            const wrapped = (...args) => {
                original.apply(console, args);
                const serializedArgs = args.map(serializeConsoleArg);
                const message = truncate(serializedArgs.map(stringifyConsoleArg).join(' '), maxStringLength);
                push({
                    level: method === 'error' ? 'error' : method === 'warn' ? 'warn' : method,
                    type: 'console',
                    source: `console.${method}`,
                    event: `console.${method}`,
                    message,
                    metadata: { arguments: serializedArgs }
                });
            };
            console[method] = wrapped;
            restoreFns.push(() => { console[method] = original; global.__appsLogsConsoleWrapped = false; });
        });
    }
    function captureFetch() {
        const global = globalThis;
        if (typeof window === 'undefined' || typeof window.fetch !== 'function')
            return;
        if (global.__appsLogsFetchWrapped)
            return;
        global.__appsLogsFetchWrapped = true;
        const originalFetch = window.fetch.bind(window);
        window.fetch = async (input, init) => {
            const startedAt = Date.now();
            const url = normalizeUrl(input);
            const method = requestMethod(input, init);
            if (isOwnIngestRequest(url, options.dsn))
                return originalFetch(input, init);
            if (isIgnoredUrl(url, options.ignoredUrls))
                return originalFetch(input, init);
            try {
                const response = await originalFetch(input, init);
                const durationMs = Date.now() - startedAt;
                const slow = durationMs >= (options.slowRequestThresholdMs || 3000);
                if (!response.ok || slow) {
                    push({
                        level: response.ok ? 'warn' : 'error',
                        type: 'network',
                        event: response.ok ? 'http.fetch.slow' : 'http.fetch.failed',
                        message: `${method} ${url || 'unknown'} ${response.status}`,
                        url,
                        http: { transport: 'fetch', method, url, status: response.status, ok: response.ok, durationMs, slow }
                    });
                }
                return response;
            }
            catch (error) {
                const durationMs = Date.now() - startedAt;
                push({
                    level: 'error',
                    type: 'network',
                    event: 'http.fetch.error',
                    message: `${method} ${url || 'unknown'} network error`,
                    url,
                    http: { transport: 'fetch', method, url, durationMs, error: error instanceof Error ? error.message : String(error) }
                });
                throw error;
            }
        };
        restoreFns.push(() => { window.fetch = originalFetch; global.__appsLogsFetchWrapped = false; });
    }
    function captureXhr() {
        const global = globalThis;
        if (typeof window === 'undefined' || typeof XMLHttpRequest === 'undefined')
            return;
        if (global.__appsLogsXhrWrapped)
            return;
        global.__appsLogsXhrWrapped = true;
        const originalOpen = XMLHttpRequest.prototype.open;
        const originalSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url, async, username, password) {
            ;
            this.__appsLogs = { method: method.toUpperCase(), url: normalizeUrl(url) };
            return originalOpen.call(this, method, url, async ?? true, username ?? null, password ?? null);
        };
        XMLHttpRequest.prototype.send = function (body) {
            const xhr = this;
            xhr.__appsLogs = xhr.__appsLogs || { method: 'GET' };
            xhr.__appsLogs.startedAt = Date.now();
            const finalize = (eventName, level, failure) => {
                const meta = xhr.__appsLogs;
                const durationMs = meta?.startedAt ? Date.now() - meta.startedAt : undefined;
                const slow = typeof durationMs === 'number' && durationMs >= (options.slowRequestThresholdMs || 3000);
                if (isOwnIngestRequest(meta?.url, options.dsn))
                    return;
                if (isIgnoredUrl(meta?.url, options.ignoredUrls))
                    return;
                if (level === 'error' || slow || eventName !== 'load') {
                    push({
                        level: level === 'error' ? 'error' : slow ? 'warn' : level,
                        type: 'network',
                        event: slow && eventName === 'load' ? 'http.xhr.slow' : `http.xhr.${eventName}`,
                        message: `${meta?.method || 'GET'} ${meta?.url || 'unknown'} ${xhr.status || failure || ''}`.trim(),
                        url: meta?.url,
                        http: { transport: 'xhr', method: meta?.method, url: meta?.url, status: xhr.status || undefined, durationMs, slow, failure }
                    });
                }
            };
            this.addEventListener('load', () => finalize('load', xhr.status >= 400 ? 'error' : 'info'), { once: true });
            this.addEventListener('error', () => finalize('error', 'error', 'network_error'), { once: true });
            this.addEventListener('timeout', () => finalize('timeout', 'error', 'timeout'), { once: true });
            this.addEventListener('abort', () => finalize('abort', 'warn', 'abort'), { once: true });
            return originalSend.call(this, body);
        };
        restoreFns.push(() => { XMLHttpRequest.prototype.open = originalOpen; XMLHttpRequest.prototype.send = originalSend; global.__appsLogsXhrWrapped = false; });
    }
    function captureWebSockets() {
        const global = globalThis;
        if (typeof window === 'undefined' || typeof WebSocket === 'undefined')
            return;
        if (global.__appsLogsWebSocketWrapped)
            return;
        global.__appsLogsWebSocketWrapped = true;
        const OriginalWebSocket = window.WebSocket;
        const WrappedWebSocket = function (url, protocols) {
            const normalizedUrl = normalizeUrl(url);
            const socket = protocols === undefined ? new OriginalWebSocket(url) : new OriginalWebSocket(url, protocols);
            push({ level: 'info', type: 'websocket', event: 'websocket.connecting', message: `WebSocket connecting ${normalizedUrl}`, url: normalizedUrl });
            socket.addEventListener('open', () => push({ level: 'info', type: 'websocket', event: 'websocket.open', message: `WebSocket open ${normalizedUrl}`, url: normalizedUrl }));
            socket.addEventListener('error', () => push({ level: 'error', type: 'websocket', event: 'websocket.error', message: `WebSocket error ${normalizedUrl}`, url: normalizedUrl }));
            socket.addEventListener('close', (event) => push({
                level: event.wasClean ? 'info' : 'warn',
                type: 'websocket',
                event: 'websocket.close',
                message: `WebSocket closed ${normalizedUrl} ${event.code}`,
                url: normalizedUrl,
                metadata: { code: event.code, reason: truncate(event.reason || '', 500), wasClean: event.wasClean }
            }));
            return socket;
        };
        WrappedWebSocket.prototype = OriginalWebSocket.prototype;
        Object.defineProperty(WrappedWebSocket, 'CONNECTING', { value: OriginalWebSocket.CONNECTING });
        Object.defineProperty(WrappedWebSocket, 'OPEN', { value: OriginalWebSocket.OPEN });
        Object.defineProperty(WrappedWebSocket, 'CLOSING', { value: OriginalWebSocket.CLOSING });
        Object.defineProperty(WrappedWebSocket, 'CLOSED', { value: OriginalWebSocket.CLOSED });
        window.WebSocket = WrappedWebSocket;
        restoreFns.push(() => { window.WebSocket = OriginalWebSocket; global.__appsLogsWebSocketWrapped = false; });
    }
    function collectDevice() {
        if (typeof navigator === 'undefined')
            return {};
        return {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            languages: navigator.languages,
            online: navigator.onLine,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            screen: typeof screen !== 'undefined' ? { width: screen.width, height: screen.height, pixelRatio: window.devicePixelRatio } : undefined,
            viewport: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : undefined
        };
    }
    function visibleScreenContext() {
        return {
            name: currentScreen?.name,
            path: currentScreen?.path || (typeof window !== 'undefined' ? window.location.pathname : undefined),
            routeName: currentScreen?.routeName,
            title: currentScreen?.title || (typeof document !== 'undefined' ? document.title : undefined),
            visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : undefined,
            dwellMs: currentScreen?.enteredAt ? Date.now() - currentScreen.enteredAt : undefined
        };
    }
    function screenViewed(name, metadata = {}) {
        const now = Date.now();
        const nextPath = metadata.path || (typeof window !== 'undefined' ? window.location.pathname : undefined);
        const nextTitle = metadata.title || (typeof document !== 'undefined' ? document.title : undefined);
        const previous = currentScreen;
        if (previous) {
            push({
                level: 'info',
                type: 'navigation',
                category: 'screen',
                event: 'screen.left',
                route: previous.path,
                message: `Left ${previous.name || previous.path || 'screen'}`,
                metadata: {
                    name: previous.name,
                    path: previous.path,
                    routeName: previous.routeName,
                    title: previous.title,
                    dwellMs: now - previous.enteredAt,
                    nextName: name,
                    nextPath,
                    reason: 'screen_change'
                }
            });
        }
        currentScreen = { name, path: nextPath, routeName: metadata.routeName, title: nextTitle, enteredAt: now };
        const { fingerprint, ...rest } = metadata;
        push({
            level: 'info',
            type: 'navigation',
            category: 'screen',
            event: 'screen.viewed',
            route: nextPath,
            message: `Viewed ${name}`,
            metadata: {
                ...rest,
                name,
                path: nextPath,
                routeName: metadata.routeName,
                title: nextTitle,
                previousName: previous?.name,
                previousPath: previous?.path,
                previousDwellMs: previous ? now - previous.enteredAt : undefined,
                visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : undefined
            },
            fingerprint: fingerprint
        });
    }
    function action(name, metadata = {}) {
        const { fingerprint, ...rest } = metadata;
        const screen = visibleScreenContext();
        push({
            level: metadata.outcome === 'failed' ? 'warn' : 'info',
            type: 'log',
            category: 'ui.action',
            event: 'ui.action',
            route: metadata.route || (typeof screen.path === 'string' ? screen.path : undefined),
            message: `User action: ${name}`,
            metadata: {
                ...rest,
                action: name,
                screen: metadata.screen || screen.name,
                screenPath: screen.path,
                screenTitle: screen.title,
                screenDwellMs: screen.dwellMs
            },
            fingerprint: fingerprint
        });
    }
    function captureStorageEvents() {
        if (typeof window === 'undefined')
            return;
        const handler = (event) => {
            if (event.storageArea !== localStorage)
                return;
            const key = event.key || '';
            const matchesConfigured = (options.storageKeys || []).some((pattern) => globMatches(key, pattern));
            const keyCategory = matchesConfigured ? 'auth' : 'other';
            // Only emit unmatched events when explicitly opted in
            if (!matchesConfigured && !options.captureUnmatchedStorageKeys)
                return;
            const storageKeyMode = options.storageKeyMode || 'full';
            const keyMetadata = storageKeyMode === 'full'
                ? { key }
                : storageKeyMode === 'hashed'
                    ? { keyHash: hashString(key) }
                    : {};
            push({
                level: keyCategory === 'auth' ? 'warn' : 'info',
                type: 'storage',
                event: 'storage.changed',
                metadata: { ...keyMetadata, keyCategory, keyMode: storageKeyMode, oldValueExists: event.oldValue != null, newValueExists: event.newValue != null }
            });
        };
        window.addEventListener('storage', handler);
        restoreFns.push(() => window.removeEventListener('storage', handler));
    }
    const logger = {
        getClientId: () => clientId,
        getSessionId: () => sessionId,
        log: (level, event, metadata) => {
            const { fingerprint, ...rest } = metadata || {};
            push({ level, type: 'log', event, message: event, metadata: rest, fingerprint: fingerprint });
        },
        debug: (event, metadata) => logger.log('debug', event, metadata),
        info: (event, metadata) => logger.log('info', event, metadata),
        warn: (event, metadata) => logger.log('warn', event, metadata),
        error: (error, metadata) => {
            const { fingerprint, ...rest } = metadata || {};
            push({ level: 'error', type: 'error', event: typeof error === 'string' ? error : 'runtime.error', message: typeof error === 'string' ? error : error.message, error: typeof error === 'string' ? undefined : { name: error.name, message: error.message, stack: error.stack }, metadata: rest, fingerprint: fingerprint });
        },
        fatal: (error, metadata) => {
            const { fingerprint, ...rest } = metadata || {};
            push({ level: 'fatal', type: 'error', event: typeof error === 'string' ? error : 'runtime.fatal', message: typeof error === 'string' ? error : error.message, error: typeof error === 'string' ? undefined : { name: error.name, message: error.message, stack: error.stack }, metadata: rest, fingerprint: fingerprint });
        },
        event: (event, metadata) => {
            const { fingerprint, ...rest } = metadata || {};
            push({ level: 'info', type: 'log', event, metadata: rest, fingerprint: fingerprint });
        },
        screen: screenViewed,
        action,
        // snapshot: named auth-state snapshot — type 'log', category 'snapshot', top-level auth field
        snapshot: (event, metadata) => {
            const { fingerprint, ...rest } = metadata || {};
            push({ level: 'info', type: 'log', category: 'snapshot', event, metadata: rest, auth: authSnapshot(), fingerprint: fingerprint });
        },
        // auth: structured auth breadcrumb — auto-merges provider snapshot, explicit auth metadata overrides
        auth: (event, auth) => {
            const { fingerprint, ...rest } = auth || {};
            push({
                level: event.includes('failed') || event.includes('missing') || event.includes('error') ? 'warn' : 'info',
                type: 'auth',
                category: 'auth',
                event,
                auth: { ...(authSnapshot() || {}), ...rest },
                fingerprint: fingerprint
            });
        },
        setAuthProvider: (provider) => { authProvider = provider; },
        identify: (nextUser) => { user = nextUser; push({ level: 'info', type: 'auth', category: 'user', event: 'user.identified', metadata: { user: nextUser } }); },
        resetUser: () => { user = undefined; push({ level: 'info', type: 'auth', category: 'user', event: 'user.reset' }); },
        setTags: (nextTags) => Object.assign(tags, nextTags),
        setContext: (key, value) => { context[key] = value; },
        destroy: () => {
            if (currentScreen) {
                push({ level: 'info', type: 'navigation', category: 'screen', event: 'screen.left', route: currentScreen.path, message: `Left ${currentScreen.name || currentScreen.path || 'screen'}`, metadata: { ...currentScreen, dwellMs: Date.now() - currentScreen.enteredAt, reason: 'logger_destroy' } }, { skipAutoFlush: true });
            }
            if (timer)
                clearInterval(timer);
            if (rateSummaryTimer)
                clearTimeout(rateSummaryTimer);
            restoreFns.splice(0).forEach((restore) => restore());
        },
        flush
    };
    if (typeof window !== 'undefined') {
        const pagehideFlush = () => { void flush(); };
        window.addEventListener('pagehide', pagehideFlush);
        restoreFns.push(() => window.removeEventListener('pagehide', pagehideFlush));
        timer = setInterval(flush, options.flushIntervalMs || 5000);
        if (options.captureIdle) {
            const activityHandler = () => { lastActivityAt = new Date(); };
            ['click', 'keydown', 'touchstart', 'scroll', 'mousemove'].forEach((name) => window.addEventListener(name, activityHandler, { passive: true }));
            restoreFns.push(() => {
                ;
                ['click', 'keydown', 'touchstart', 'scroll', 'mousemove'].forEach((name) => window.removeEventListener(name, activityHandler));
            });
        }
        if (options.captureLifecycle) {
            const pageshowHandler = (event) => push({ level: 'info', type: 'lifecycle', event: 'app.pageshow', metadata: { persisted: event.persisted } });
            const pagehideLifecycleHandler = (event) => push({ level: 'info', type: 'lifecycle', event: 'app.pagehide', metadata: { persisted: event.persisted } });
            const visibilityHandler = () => {
                if (document.visibilityState === 'hidden') {
                    lastHiddenAt = new Date();
                    push({ level: 'info', type: 'lifecycle', event: 'app.page_hidden' });
                }
                else
                    push({ level: 'info', type: 'lifecycle', event: 'app.page_visible', idle: idleMetadata() });
            };
            const onlineHandler = () => push({ level: 'info', type: 'lifecycle', event: 'app.online' });
            const offlineHandler = () => push({ level: 'warn', type: 'lifecycle', event: 'app.offline' });
            const freezeHandler = () => push({ level: 'info', type: 'lifecycle', event: 'app.freeze' });
            const resumeHandler = () => push({ level: 'info', type: 'lifecycle', event: 'app.resume' });
            window.addEventListener('pageshow', pageshowHandler);
            window.addEventListener('pagehide', pagehideLifecycleHandler);
            document.addEventListener('visibilitychange', visibilityHandler);
            window.addEventListener('online', onlineHandler);
            window.addEventListener('offline', offlineHandler);
            document.addEventListener('freeze', freezeHandler);
            document.addEventListener('resume', resumeHandler);
            restoreFns.push(() => {
                window.removeEventListener('pageshow', pageshowHandler);
                window.removeEventListener('pagehide', pagehideLifecycleHandler);
                document.removeEventListener('visibilitychange', visibilityHandler);
                window.removeEventListener('online', onlineHandler);
                window.removeEventListener('offline', offlineHandler);
                document.removeEventListener('freeze', freezeHandler);
                document.removeEventListener('resume', resumeHandler);
            });
        }
        if (options.captureErrors) {
            const errorHandler = (e) => logger.error(e.error || e.message, { source: 'window.error', filename: e.filename, line: e.lineno, column: e.colno });
            const rejectionHandler = (e) => logger.error(e.reason instanceof Error ? e.reason : String(e.reason), { source: 'window.unhandledrejection' });
            window.addEventListener('error', errorHandler);
            window.addEventListener('unhandledrejection', rejectionHandler);
            restoreFns.push(() => {
                window.removeEventListener('error', errorHandler);
                window.removeEventListener('unhandledrejection', rejectionHandler);
            });
        }
        if (options.captureConsole)
            captureConsole();
        if (options.captureNetwork) {
            captureFetch();
            captureXhr();
        }
        if (options.captureWebSockets)
            captureWebSockets();
        if (options.captureStorage)
            captureStorageEvents();
    }
    push({
        level: 'info',
        type: 'lifecycle',
        event: 'sdk.initialized',
        metadata: {
            dsnOrigin: safeDsnOrigin(options.dsn),
            environment: options.environment,
            release: options.release,
            captures: {
                console: !!options.captureConsole,
                errors: !!options.captureErrors,
                network: !!options.captureNetwork,
                webSockets: !!options.captureWebSockets,
                lifecycle: !!options.captureLifecycle,
                idle: !!options.captureIdle,
                storage: !!options.captureStorage
            }
        }
    }, { internal: true, skipDedupe: true, skipRateLimit: true, skipAutoFlush: true });
    return logger;
}
